import React, { useState, useCallback, useLayoutEffect, useEffect, useMemo } from 'react';
import { Alert, View } from 'react-native';
import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { IconProfile, IconBoldCalendar } from 'design-system/icons';
import Button from 'design-system/Button';
import { SettingsSection } from 'design-system/SettingsSection';
import { SettingsItemNavigate } from 'design-system/SettingsItem';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import { SCROLL_INDICATOR_INSETS } from 'design-system/values';
import useColors from 'hooks/useColors';
import { isUnder18 } from 'utils/validation';
import { DOB_DISPLAY_FORMAT } from 'hooks/useDateTimePickerDOB';
import { OnboardingFlow } from 'utils/types/navigation';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectPhoneNumberRecentlyVerified, selectUser } from 'reducers/selectors';
import { getUser } from 'actions';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { AppView, NavigationHeader } from 'design-system';
import { TitleModal } from 'features/borrow/components/monevo/TitleModal';
import { TITLES } from 'features/borrow/monevo/models';
import LeaveOnboardingButton from 'features/wealth/components/LeaveOnboardingButton';
import { MIDDLE_NAME_REQUIRED } from '../constants';
import { useCommonInvestStyles } from './styles';
import { useAmplitudeOnMountEvent } from '../utils/analytics';
const strings = {
    alertTitle: 'Hold on 💫',
    alertMissingFirstName: 'You need to specify your first name.',
    alertMissingMiddleName: 'You need to specify your middle name.',
    alertMissingLastName: 'You need to specify your last name.',
    alertMissingDob: 'You need to specify your date of birth.',
    alert18invest: 'You need to be 18 or older in order to invest with Emma!',
    alert18pots: 'You need to be 18 or older in order to open a pots account!',
    alert18wallet: 'You need to be 18 or older in order to open Emma wallet!',
    titleRow: 'Title',
    firstName: 'Legal first name',
    middleName: 'Legal middle name (optional)',
    lastName: 'Legal last name',
    dob: 'Date of birth',
    defaultTitle: 'Add...',
    buttonNext: 'Continue',
};
const PersonalDetailsScreen = ({ route, navigation }) => {
    const flow = route.params?.flow;
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const commonStyles = useCommonInvestStyles(!!mountedOnAnotherScreen);
    const scrollY = useSharedValue(0);
    const user = useAppSelector(selectUser);
    const isPhoneNumberRecentlyVerified = useAppSelector(selectPhoneNumberRecentlyVerified);
    const dispatch = useAppDispatch();
    useEffect(() => {
        dispatch(getUser());
    }, []);
    const colors = useColors();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, flow]);
    const [title, setTitle] = useState(user?.title ?? '');
    const [firstName, setFirstName] = useState(user?.firstName ?? '');
    const [middleName, setMiddleName] = useState(user?.middleName ?? '');
    const [lastName, setLastName] = useState(user?.lastName ?? '');
    const [isTitleModalOpen, setIsTitleModalOpen] = useState(false);
    const titleLabel = TITLES.find((t) => t.value === title)?.label || strings.defaultTitle;
    const openTitle = useCallback(() => {
        setIsTitleModalOpen(true);
        TitleModal.show((titleObj) => {
            setTitle(titleObj.value);
            setIsTitleModalOpen(false);
        }, undefined, () => {
            setIsTitleModalOpen(false);
        });
    }, []);
    useAmplitudeOnMountEvent(flow, 'User Info Screen');
    const handleDobPress = useCallback(() => {
        navigation.navigateDeprecated('DateOfBirth', {
            requiredAge: 18,
            flow: route.params?.flow || OnboardingFlow.Invest,
        });
    }, [route.params?.flow]);
    const onSubmit = useCallback(() => {
        if (firstName.trim().length === 0) {
            Alert.alert(strings.alertTitle, strings.alertMissingFirstName);
            return false;
        }
        if (middleName.trim().length === 0 && MIDDLE_NAME_REQUIRED) {
            Alert.alert(strings.alertTitle, strings.alertMissingMiddleName);
            return false;
        }
        if (lastName.trim().length === 0) {
            Alert.alert(strings.alertTitle, strings.alertMissingLastName);
            return false;
        }
        const userDob = user?.dateOfBirth;
        if (!userDob) {
            Alert.alert(strings.alertTitle, strings.alertMissingDob);
            return false;
        }
        if (isUnder18(toZonedTime(userDob, 'UTC'))) {
            const ageError = flow === OnboardingFlow.Pots
                ? strings.alert18pots
                : flow === OnboardingFlow.Wallet
                    ? strings.alert18wallet
                    : strings.alert18invest;
            Alert.alert(strings.alertTitle, ageError);
            return false;
        }
        const postData = {};
        if (firstName.trim() !== user?.firstName) {
            postData.firstName = firstName.trim();
        }
        if (lastName.trim() !== user?.lastName) {
            postData.lastName = lastName.trim();
        }
        if (middleName.trim() !== user?.middleName) {
            if (middleName.trim().length !== 0 || user?.middleName) {
                postData.middleName = middleName.trim();
            }
        }
        if (title.trim() !== user?.title) {
            postData.title = title.trim();
        }
        // No phone number — verify one before moving on
        if (!user?.phoneNumber) {
            navigation.navigateDeprecated('PhoneVerification', {
                flow: flow === OnboardingFlow.Pots ? 'save' : 'invest',
                tintColor: colors.text.brand,
                onSuccess: () => {
                    navigation.pop(2);
                    navigation.navigateDeprecated('InvestCitizenshipDetails', {
                        flow,
                        title: title.trim(),
                        firstName: firstName.trim(),
                        middleName: middleName.trim(),
                        lastName: lastName.trim(),
                        dob: format(toZonedTime(userDob, 'UTC'), DOB_DISPLAY_FORMAT),
                        postData,
                    });
                },
            });
            return true;
        }
        // Number not verified recently (or verification date unknown) — ask the
        // user to confirm it
        if (!isPhoneNumberRecentlyVerified) {
            navigation.navigateDeprecated('InvestContactDetails', {
                flow: flow,
                firstName: firstName.trim(),
                middleName: middleName.trim(),
                lastName: lastName.trim(),
                title: title.trim(),
                postData,
            });
        }
        else {
            navigation.navigateDeprecated('InvestCitizenshipDetails', {
                flow,
                firstName: firstName.trim(),
                middleName: middleName.trim(),
                title: title.trim(),
                lastName: lastName.trim(),
                dob: format(toZonedTime(userDob, 'UTC'), DOB_DISPLAY_FORMAT),
                postData,
            });
        }
        return true;
    }, [
        firstName,
        middleName,
        lastName,
        user?.dateOfBirth,
        user?.firstName,
        user?.lastName,
        user?.middleName,
        user?.title,
        user?.phoneNumber,
        isPhoneNumberRecentlyVerified,
        title,
        flow,
        colors.text.brand,
    ]);
    const changeName = useCallback(() => {
        navigation.navigateDeprecated('ChangeDetail', {
            key: 'firstName',
            defaultValue: firstName,
            onSuccess: setFirstName,
        });
    }, [firstName]);
    const changeMiddleName = useCallback(() => {
        navigation.navigateDeprecated('ChangeDetail', {
            key: 'middleName',
            defaultValue: middleName,
            onSuccess: setMiddleName,
        });
    }, [middleName]);
    const changeLastName = useCallback(() => {
        navigation.navigateDeprecated('ChangeDetail', {
            key: 'lastName',
            defaultValue: lastName,
            onSuccess: setLastName,
        });
    }, [lastName]);
    const areFieldsDisabled = Boolean(user.kycStartedAt || user.creditScoreIdentityLockedAt);
    const isButtonDisabled = useMemo(() => !firstName.trim() || !lastName.trim() || !user?.dateOfBirth || !title, [firstName, lastName, title, user?.dateOfBirth]);
    const outputColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler((event) => {
        scrollY.value = event.contentOffset.y;
    });
    return (<AppView withFrame={false}>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={outputColors} isInTab={!!mountedOnAnotherScreen} leftContent={mountedOnAnotherScreen ? <LeaveOnboardingButton flow={flow}/> : undefined} title="" rightContent={<View style={!!mountedOnAnotherScreen && commonStyles.flexEnd}>
            <HelpScreenButton articlesKey={flow === OnboardingFlow.Pots ? HELP_ARTICLES_KEY.POTS_ONBOARDING : undefined}/>
          </View>}/>
      <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} keyboardShouldPersistTaps="handled" scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={commonStyles.contentContainer}>
        <ScreenInfoHeader title="Personal details" subTitle="Please make sure your details are accurate and up to date"/>
        <SettingsSection noMarginTop>
          <SettingsItemNavigate title={titleLabel} onPress={openTitle} isSelected={isTitleModalOpen} description={strings.titleRow} icon={<IconProfile gradientKey="time"/>}/>
          <SettingsItemNavigate icon={<IconProfile gradientKey="darkPurple"/>} disabled={areFieldsDisabled} description={strings.firstName} title={firstName || strings.defaultTitle} onPress={changeName}/>
          <SettingsItemNavigate icon={<IconProfile gradientKey="lightPink"/>} disabled={areFieldsDisabled} description={strings.middleName} title={middleName || strings.defaultTitle} onPress={changeMiddleName}/>
          <SettingsItemNavigate icon={<IconProfile gradientKey="skyBlue"/>} disabled={areFieldsDisabled} description={strings.lastName} title={lastName || strings.defaultTitle} onPress={changeLastName}/>
          <SettingsItemNavigate onPress={handleDobPress} description={strings.dob} disabled={areFieldsDisabled ? Boolean(user?.dateOfBirth) : undefined} icon={<IconBoldCalendar gradientColors={colors.gradients.darkPurple.colors}/>} title={user?.dateOfBirth
            ? format(toZonedTime(user.dateOfBirth, 'UTC'), DOB_DISPLAY_FORMAT)
            : strings.defaultTitle}/>
        </SettingsSection>
      </Animated.ScrollView>
      <Button brand floating onPress={onSubmit} title={strings.buttonNext} disabled={isButtonDisabled} isInTab={mountedOnAnotherScreen}/>
    </AppView>);
};
export default PersonalDetailsScreen;
